import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MdArrowBack } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import './milestone.css';

const MEDIA_PREFIX = process.env.REACT_APP_API_BASE.replace("/v1", "");

function parseDate(dateString) {
    if (!dateString) return new Date(NaN);
    try {
        const d = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
        return isNaN(d) ? new Date(dateString) : d;
    } catch {
        return new Date(dateString);
    }
}

function getWeekKey(dateString) {
    const dateObj = parseDate(dateString);
    if (isNaN(dateObj)) return 'unknown';
    const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
    return format(weekStart, 'yyyy-MM-dd');
}

function getWeekLabel(weekKey) {
    if (weekKey === 'unknown') return 'Unknown';
    const weekStart = parseISO(weekKey);
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, 'd')} - ${format(weekEnd, 'd MMM')}`;
    }
    return `${format(weekStart, 'd MMM')} - ${format(weekEnd, 'd MMM')}`;
}

function resolveImageUrl(img) {
    let url = img.image ? img.image : img;
    if (url && !/^https?:\/\//.test(url)) {
        url = MEDIA_PREFIX + url;
    }
    return url;
}

const MilestoneView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const milestone = location.state?.milestone;
    const allMilestones = location.state?.allMilestones;

    // Group milestones by week key, preserving order of first occurrence
    const { weekGroups, weekKeys } = useMemo(() => {
        if (!Array.isArray(allMilestones)) return { weekGroups: {}, weekKeys: [] };
        const groups = {};
        const keys = [];
        allMilestones.forEach(m => {
            const key = getWeekKey(m.date || m.created_at);
            if (!groups[key]) {
                groups[key] = [];
                keys.push(key);
            }
            groups[key].push(m);
        });
        return { weekGroups: groups, weekKeys: keys };
    }, [allMilestones]);

    // Find initial week key from the milestone that was clicked
    const initialWeekKey = useMemo(() => {
        if (!milestone) return weekKeys[0] || 'unknown';
        const key = getWeekKey(milestone.date || milestone.created_at);
        return weekKeys.includes(key) ? key : (weekKeys[0] || 'unknown');
    }, [milestone, weekKeys]);

    const [selectedWeekKey, setSelectedWeekKey] = useState(initialWeekKey);

    const selectedMilestones = weekGroups[selectedWeekKey] || [];

    // Scroll to the clicked milestone within the week group
    const milestoneRefs = useRef({});
    useEffect(() => {
        if (!milestone?.id) return;
        const id = milestone.id;
        const timer = setTimeout(() => {
            const el = milestoneRefs.current[id];
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [selectedWeekKey, milestone]);

    const handleBack = () => navigate(-1);

    if (!milestone && !allMilestones) {
        return (
            <div className="milestone-container milestone-container-custom">
                <p className="milestone-no-images-custom">No milestone data available.</p>
            </div>
        );
    }

    return (
        <div className="milestone-container">
            {/* Header bar with back + dropdown */}
            <div className="milestone-header-bar-custom">
                <MdArrowBack
                    className="back-icon milestone-back-icon-custom"
                    onClick={handleBack}
                />
                <div className="milestone-dropdown-wrapper">
                    <select
                        className="milestone-week-select"
                        value={selectedWeekKey}
                        onChange={e => setSelectedWeekKey(e.target.value)}
                    >
                        {weekKeys.map(key => (
                            <option key={key} value={key}>{getWeekLabel(key)}</option>
                        ))}
                    </select>
                    <svg className="milestone-dropdown-icon" viewBox="0 0 20 20">
                        <path d="M7 8l3 3 3-3" stroke="#888" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {/* Milestones for selected week — stacked vertically */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '16px 0' }}>
                {selectedMilestones.map((m, idx) => {
                    const images = Array.isArray(m.images)
                        ? m.images.map(resolveImageUrl)
                        : [];

                    return (
                        <div key={m.id || idx} ref={el => { if (m.id) milestoneRefs.current[m.id] = el; }}>
                            <div className="milestone-header">
                                <div className="milestone-header-content">
                                    <div>
                                        <h1 className="milestone-title">{m.title || `Milestone`}</h1>
                                        <p className="milestone-subtitle">{m.announcement || m.text || m.description || ''}</p>
                                    </div>
                                </div>
                            </div>

                            {images.length > 0 && (
                                <div
                                    className="milestone-images"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '10px',
                                        overflowX: 'auto',
                                        paddingBottom: '8px',
                                    }}
                                >
                                    {images.map((img, i) => (
                                        <img
                                            key={i}
                                            src={img}
                                            alt="Milestone"
                                            className="milestone-thumb"
                                            style={{ flexShrink: 0 }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MilestoneView;
